import {
  Image,
  Button,
  Col,
  Row,
  Typography,
  Input,
  Form,
  theme,
  message,
  Space,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../Theme";
import { authorizeActions, authorizeSelectors } from "../store/authorize";
import { useDispatch, useSelector } from "../store/store";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useRef, useState } from "react";
import {
  updateProfile,
  uploadAvatar,
  getAvatar,
} from "../repository/auth.repository";
import { UserInput } from "../repository/auth.default";
import BlankAvatar from "../assets/images/blank-avatar.svg";
import _ from "lodash";
import ResetPasswordWizard from "../components/ResetPasswordWizard";
import Icon from "@ant-design/icons";
import { Icons } from "../assets/icons";

function CompanyProfilePage() {
  const navigate = useNavigate();
  const avatarRef = useRef<HTMLInputElement>(null!);

  const { token } = theme.useToken();
  const { change, isDark } = useTheme();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const [messageApi, contextHolder] = message.useMessage();

  const [valuesValid, setValuesValid] = useState(false);
  const [valuesChange, setValuesChange] = useState<any>(null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [savingChanges, setSavingChanges] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);

  const dispatch = useDispatch();
  const user = useSelector(authorizeSelectors.user);

  // Lifecycle

  useEffect(() => {
    if (!avatarLoading) {
      getAvatar()
        .catch(() => BlankAvatar)
        .then((data) => {
          dispatch(
            authorizeActions.setUser({ ...user, avatarSrc: data } as any),
          );
        });
    }
  }, [avatarLoading]);

  useEffect(() => {
    if (valuesChange == null) {
      setValuesChange(values);
    }
    setValuesValid(!form.getFieldsError().some(({ errors }) => errors.length));
  }, [values]);

  // Events

  function onBack(): void {
    navigate("../");
  }

  function onToggleTheme(): void {
    change(!isDark);
  }

  function onSaveChanges(): void {
    const values: UserInput = form.getFieldsValue();
    setSavingChanges(true);
    updateProfile(values)
      .then(() => {
        setSavingChanges(false);
        setValuesChange(values);
        dispatch(
          authorizeActions.setUser({
            ...user!,
            ...values,
          }),
        );
      })
      .catch(() => {
        setSavingChanges(false);
      });
  }

  function onCancelChanges(): void {
    form.setFieldsValue(valuesChange);
  }

  function onPickAvatar(): void {
    avatarRef.current.click();
  }

  function onUploadAvatar(e: React.ChangeEvent): void {
    const file = (e.target as any).files[0];
    if (file) {
      const mb = file.size / 1024 / 1024;
      const limit = 10;
      if (mb > limit) {
        messageApi.open({
          type: "error",
          content: "Avatar size limit is 10mb",
        });
      } else {
        setAvatarLoading(true);
        uploadAvatar(file)
          .then(() => {
            setAvatarLoading(false);
            messageApi.open({
              type: "success",
              content: "Successfully uploaded avatar",
            });
          })
          .catch(() => {
            setAvatarLoading(false);
            messageApi.open({
              type: "error",
              content: "Failed to upload avatar",
            });
          });
      }
    }
  }

  function onResetPasswordOpen(): void {
    setResetPasswordOpen(true);
  }

  function onResetPasswordClose(): void {
    setResetPasswordOpen(false);
  }

  // Methods

  function getProfileProps(value: any): UserInput {
    value = value || {};
    return {
      firstName: value.firstName,
      lastName: value.lastName,
      middleName: value.middleName,
      phoneNumber: value.phoneNumber,
      email: value.email,
      companyPhoneNumber: value.companyPhoneNumber,
      aboutMe: value.aboutMe,
    };
  }

  const avatarSrc = user?.avatarSrc ? user?.avatarSrc : BlankAvatar;
  const hasChanges = !_.isEqual(valuesChange, values);

  return (
    <Col className="flex flex-col overflow-hidden">
      {/* Panel */}
      <Row className="flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Profile
        </Typography.Title>
        <Space className="flex flex-row">
          <Button
            type="primary"
            loading={savingChanges || avatarLoading}
            disabled={!hasChanges || !valuesValid}
            onClick={onSaveChanges}
          >
            Save
          </Button>
          <Button disabled={!hasChanges} onClick={onCancelChanges}>
            Cancel
          </Button>
          <Button
            className="ml-4"
            type={"text"}
            icon={<Icon className="text-xl" component={Icons.CloseIcon} />}
            onClick={onBack}
          />
        </Space>
      </Row>
      {/* Content */}
      <Col className="flex flex-col overflow-hidden overflow-y-auto p-4 pt-0">
        <Form
          className="my-form-item flex flex-col"
          form={form}
          layout={"vertical"}
          variant={"filled"}
          colon={false}
          initialValues={getProfileProps(user)}
          autoComplete="off"
          disabled={savingChanges || avatarLoading}
        >
          {/* Avatar */}
          <Row className="flex flex-row">
            <Col className="relative mb-4 flex flex-col">
              <Image width={128} height={128} src={avatarSrc} alt="Avatar" />
              <input
                className="hidden"
                ref={avatarRef}
                hidden={true}
                type="file"
                accept="image/png, image/jpeg"
                onChange={onUploadAvatar}
              />
              <Col className="absolute -bottom-2 -right-2 opacity-50 hover:opacity-100">
                <Button
                  shape="circle"
                  size="small"
                  icon={<FontAwesomeIcon icon={faImage} />}
                  loading={avatarLoading}
                  onClick={onPickAvatar}
                />
              </Col>
            </Col>
          </Row>
          <Typography.Title className="mt-0" level={5}>
            Identity
          </Typography.Title>
          {/* Identity */}
          <Row className="flex flex-row">
            <Col className="flex flex-col">
              <Form.Item
                className="m-0.5 w-96"
                name={"firstName"}
                label={
                  <Typography.Text
                    className="text-sm"
                    style={{
                      color: token.colorTextDescription,
                    }}
                  >
                    First Name
                  </Typography.Text>
                }
              >
                <Input />
              </Form.Item>
              <Form.Item
                className="m-0.5 w-96"
                name={"middleName"}
                label={
                  <Typography.Text
                    className="text-sm"
                    style={{
                      color: token.colorTextDescription,
                    }}
                  >
                    Middle Name
                  </Typography.Text>
                }
              >
                <Input />
              </Form.Item>
              <Form.Item
                className="m-0.5 w-96"
                name={"lastName"}
                label={
                  <Typography.Text
                    className="text-sm"
                    style={{
                      color: token.colorTextDescription,
                    }}
                  >
                    Last Name
                  </Typography.Text>
                }
              >
                <Input />
              </Form.Item>
              <Form.Item
                className="m-0.5 w-96"
                name={"aboutMe"}
                label={
                  <Typography.Text
                    className="text-sm"
                    style={{
                      color: token.colorTextDescription,
                    }}
                  >
                    About Me
                  </Typography.Text>
                }
              >
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
          {/* Contact Info */}
          <Typography.Title className="mt-4" level={5}>
            Contact Info
          </Typography.Title>
          <Col className="flex flex-col">
            <Form.Item
              className="m-0.5 w-96"
              name={"email"}
              rules={[{ type: "email", message: "Email is invalid" }]}
              label={
                <Typography.Text
                  className="text-sm"
                  style={{
                    color: token.colorTextDescription,
                  }}
                >
                  Email
                </Typography.Text>
              }
            >
              <Input />
            </Form.Item>
            <Form.Item
              className="m-0.5 w-96"
              name={"phoneNumber"}
              rules={[
                {
                  message: "Phone number is invalid",
                  validator: (_, value) => {
                    if (/\+?[0-9]+$/.test(value)) {
                      return Promise.resolve();
                    } else {
                      return Promise.reject("Phone number is invalid");
                    }
                  },
                },
              ]}
              label={
                <Typography.Text
                  className="text-sm"
                  style={{
                    color: token.colorTextDescription,
                  }}
                >
                  Phone
                </Typography.Text>
              }
            >
              <Input />
            </Form.Item>
            <Form.Item
              className="m-0.5 w-96"
              name={"companyPhoneNumber"}
              rules={[
                {
                  message: "Phone number is invalid",
                  validator: (_, value) => {
                    if (/\+?[0-9]+$/.test(value)) {
                      return Promise.resolve();
                    } else {
                      return Promise.reject("Phone number is invalid");
                    }
                  },
                },
              ]}
              label={
                <Typography.Text
                  className="text-sm"
                  style={{
                    color: token.colorTextDescription,
                  }}
                >
                  Company Phone
                </Typography.Text>
              }
            >
              <Input />
            </Form.Item>
          </Col>
          <Typography.Title className="mt-4" level={5}>
            Password
          </Typography.Title>
          {/* Reset */}
          <Row className="flex flex-row">
            <Button danger onClick={onResetPasswordOpen}>
              Reset Password
            </Button>
          </Row>
        </Form>
      </Col>
      {/* Reset Password Modal */}
      <ResetPasswordWizard
        open={resetPasswordOpen}
        onCancel={onResetPasswordClose}
      />
      {contextHolder}
    </Col>
  );
}

export default CompanyProfilePage;
